/**
 * @typedef {import("../../types/directus").components["schemas"]["ItemsArticles"]} DirectusArticle
 * @typedef {import("../../types/directus").components["schemas"]["x-metadata"]} DirectusMeta
 */
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { getJson } from "../client";

function buildFilter({ featured, search }) {
  const filter = { status: { _eq: "published" } };
  if (typeof featured === "boolean") filter.featured = { _eq: featured };
  if (search) {
    // Search through nested translation content using correct Directus syntax
    filter._or = [
      { translations: { content: { _contains: search } } },
      { translations: { title: { _contains: search } } },
    ];
  }
  return filter;
}

async function fetchArticles({ page = 1, limit = 12, search, featured }) {
  const fields = [
    "id",
    "status",
    "cover_image",
    "read_time",
    "published_at",
    "featured",
    // Countries - fetch nested data
    "countries.Countries_id.id",
    "countries.Countries_id.key",
    "countries.Countries_id.translations.name",
    "countries.Countries_id.translations.languages_code",

    // Translations - fetch nested data
    "translations.id",
    "translations.title",
    "translations.content",
    "translations.languages_code",
    // Author via junction -> Author
    "author.id",
    "author.Author_id.id",
    "author.Author_id.first_name",
    "author.Author_id.last_name",
    "author.Author_id.avatar",
    "author.Author_id.translations.id",
    "author.Author_id.translations.languages_code",
    "author.Author_id.translations.description",
    "author.Author_id.instagram",
    "author.Author_id.face",
    // Tags via junction -> Tags
    "tags.id",
    "tags.Tags_id.id",
    "tags.Tags_id.name",
    "tags.Tags_id.translations",
    "tags.Tags_id.translations.languages_code",
    "tags.Tags_id.translations.text",
  ].join(",");

  const params = {
    fields,
    sort: "-published_at",
    limit: String(limit),
    page: String(page),
    filter: buildFilter({ featured, search }),
    // Required for pagination — Directus returns filter_count / total_count
    meta: "filter_count",
  };

  return getJson("/items/Articles", params);
}

export function useArticles(
  { page = 1, limit = 12, search, featured } = {},
  options = {}
) {
  const queryKey = [
    "articles",
    {
      page,
      limit,
      search: search || "",
      featured: featured ?? "any",
    },
  ];

  return useQuery({
    queryKey,
    /** @returns {Promise<{ data?: DirectusArticle[]; meta?: DirectusMeta }>} */
    queryFn: () => fetchArticles({ page, limit, search, featured }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useInfiniteArticles(
  { limit = 9, search, featured } = {},
  options = {}
) {
  return useInfiniteQuery({
    queryKey: [
      "articles",
      "infinite",
      {
        limit,
        search: search || "",
        featured: featured ?? "any",
      },
    ],
    /** @returns {Promise<{ data?: DirectusArticle[]; meta?: DirectusMeta }>} */
    queryFn: ({ pageParam = 1 }) =>
      fetchArticles({ page: pageParam, limit, search, featured }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce(
        (sum, page) => sum + (page?.data?.length || 0),
        0
      );
      const total = lastPage?.meta?.filter_count ?? loaded;
      return loaded < total ? allPages.length + 1 : undefined;
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    ...options,
  });
}

export default useArticles;
